import type { RawMessage } from "@/api/messages";
import type { PmUser } from "@/api/user";
import type { RecoveryTrigger } from "@/api/recoveryOffer";
import { getLetterPhotoType, type CheckoutRequirements } from "@/checkout/requirements";
import { getIsResend } from "@/lib/flags";

// In-session checkout recovery: the client decides only WHEN to ask (an
// explicit abandon action on a paid checkout); the server decides WHETHER
// there is an offer. Nothing here reads the coupon machinery or
// `user.hasSentLetter` — eligibility lives on durable server-side facts.

// Kill lever: in-code ON; env CHECKOUT_RECOVERY_ENABLED="false"/"0" disables
// every trigger (no request, no sheet, no events).
export const checkoutRecoveryEnabled = (): boolean => {
  try {
    const v = (window as any)?.Penmate?.env?.checkoutRecoveryEnabled;
    if (typeof v === "string" && v.trim()) {
      const s = v.trim().toLowerCase();
      return s !== "false" && s !== "0";
    }
    return true;
  } catch {
    return true;
  }
};

// The one client-side line on the surface besides the photos badge: shown
// when accept fails (or when the server's error is a bare token).
export const RECOVERY_ACCEPT_ERROR_MESSAGE = "We couldn't apply the offer. Please try again.";
export const RECOVERY_RESCUE_ERROR_MESSAGE = "We couldn't make this message free. Please try again.";
// The server's refusal is final (the rescue was used, or the letter no
// longer qualifies): no retry is offered.
export const RECOVERY_RESCUE_UNAVAILABLE_MESSAGE = "This message can no longer be sent free.";

// The server marks a letter that carries an accepted recovery offer; the
// client reads it only to recognise an accept that landed after a timeout
// and to keep an accepted letter off the glossy/credits path.
export const letterHasRecoveryOffer = (letter: unknown): boolean => {
  const l = letter as any;
  const v = l?.checkout_recovery ?? l?.data?.checkout_recovery;
  return !!v && typeof v === "object";
};

// The letter read carries the same `recovery_offer` field the drafts read
// does. An OPEN issuance on it means the customer has already seen the sheet
// and chose "Not now": the checkout card then shows a reminder line
// (server-owned label) that re-presents the sheet on tap. Nothing renders on
// first arrival (no issuance yet), for holdouts, or once the offer is
// applied. Only the whole line is trusted: no id or no label, no line.
export type RecoveryReminder = { issuanceId: string; label: string };

export const letterRecoveryReminder = (letter: unknown): RecoveryReminder | null => {
  if (!checkoutRecoveryEnabled()) return null;
  const l = letter as any;
  if (!l || typeof l !== "object" || letterHasRecoveryOffer(l)) return null;
  const field = l.recovery_offer;
  if (!field || typeof field !== "object") return null;
  const rawId = (field as any).issuance_id;
  const issuanceId =
    typeof rawId === "string" && rawId.trim() ? rawId.trim() : typeof rawId === "number" ? String(rawId) : "";
  const label = typeof (field as any).reminder_label === "string" ? (field as any).reminder_label.trim() : "";
  if (!issuanceId || !label) return null;
  return { issuanceId, label };
};

// Once the offer is applied the same field switches to its APPLIED shape:
// server-owned labels and numbers (the regular price this letter would cost
// as it is now, and the saving), derived server-side from the marker's terms
// so a change of promo amount needs nothing here. Rendered only on a letter
// that carries the marker, and only when both labels are present. The
// numbers can be missing (the server's re-price is best-effort): the
// "applied" state still shows, label-only, with no amounts.
export type RecoveryApplied = {
  label: string;
  discountLabel: string;
  regularPriceCents: number | null;
  savingsCents: number | null;
};

const nonNegativeCents = (value: unknown): number | null => {
  const n = typeof value === "number" ? value : typeof value === "string" && value.trim() ? Number(value) : NaN;
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n);
};

export const letterRecoveryApplied = (letter: unknown): RecoveryApplied | null => {
  if (!checkoutRecoveryEnabled()) return null;
  const l = letter as any;
  if (!l || typeof l !== "object" || !letterHasRecoveryOffer(l)) return null;
  const field = l.recovery_offer;
  if (!field || typeof field !== "object" || (field as any).applied !== true) return null;
  const label = typeof (field as any).applied_label === "string" ? (field as any).applied_label.trim() : "";
  const discountLabel = typeof (field as any).discount_label === "string" ? (field as any).discount_label.trim() : "";
  if (!label || !discountLabel) return null;
  return {
    label,
    discountLabel,
    regularPriceCents: nonNegativeCents((field as any).regular_price_cents),
    savingsCents: nonNegativeCents((field as any).savings_cents),
  };
};

// The trigger request is bounded so an abandon action never feels stuck; a
// late response is dropped and the server's idempotent issuance re-returns
// the same open offer on the next trigger.
export const RECOVERY_REQUEST_TIMEOUT_MS = 1500;
// The return surface presents on arrival and races nothing: a slow network
// must not turn the tap into a dead end.
export const RECOVERY_RETURN_TIMEOUT_MS = 8000;

export const recoveryTriggerKind = (trigger: RecoveryTrigger): "in_session" | "return" =>
  trigger === "return" ? "return" : "in_session";

// Regime D exit-certainty ladder, per letter for the life of the bundle
// (in-session = this page load): the 1st abandonment of a letter presents
// the sweetener; the 2nd or later — or a host/hardware-back leave after the
// sweetener was displayed — presents the rescue, when the server's read
// says one is available. A recovery holdout never sees a sweetener and
// reaches the same rung by the same second abandonment (price → free).
// The server owns IF (arm, once-ever, text-only); this state only decides
// WHEN.
type RecoveryLadderState = { abandonments: number; sweetenerDisplayed: boolean };
const ladderByLetter = new Map<string, RecoveryLadderState>();
const ladderFor = (letterId: string): RecoveryLadderState => {
  let s = ladderByLetter.get(letterId);
  if (!s) {
    s = { abandonments: 0, sweetenerDisplayed: false };
    ladderByLetter.set(letterId, s);
  }
  return s;
};
export const recordRecoveryAbandonment = (letterId: string): number => {
  const s = ladderFor(letterId);
  s.abandonments += 1;
  return s.abandonments;
};
export const markRecoverySweetenerDisplayed = (letterId: string): void => {
  ladderFor(letterId).sweetenerDisplayed = true;
};
export const recoveryRescueIntent = (args: {
  letterId: string;
  trigger: RecoveryTrigger;
  abandonments: number;
}): boolean => {
  if (args.abandonments >= 2) return true;
  return args.trigger === "host_back" && ladderFor(args.letterId).sweetenerDisplayed;
};
export const resetRecoveryLadderForTests = (): void => ladderByLetter.clear();

// The reads carry the rescue flag precisely so exit-certainty can be decided
// WITHOUT a speculative POST: no rescue on the cached letter = the moment
// behaves exactly as it did before regime D. The trigger response remains
// the authority when a request does go out.
export const letterRescueAvailable = (letter: unknown): boolean => {
  const field = (letter as any)?.recovery_offer;
  return !!field && typeof field === "object" && (field as any).rescue_available === true;
};

// The rescue presented on its own, when no recovery offer is there to carry
// it (a recovery holdout or recovery-ineligible customer, the recovery lever
// off, a lost request): the read ships the sheet's copy next to the flag.
// Server-owned like every other line; any missing line means no sheet.
export type RescueCopy = {
  headline: string;
  body: string;
  rescueLabel: string;
  rescueDetail: string | null;
  declineLabel: string;
};

export const letterRescueCopy = (letter: unknown): RescueCopy | null => {
  if (!letterRescueAvailable(letter)) return null;
  const raw = (letter as any).recovery_offer.rescue_copy;
  if (!raw || typeof raw !== "object") return null;
  const line = (value: unknown): string => (typeof value === "string" && value.trim() ? value.trim() : "");
  const headline = line(raw.headline);
  const body = line(raw.body);
  const rescueLabel = line(raw.rescue_label);
  const declineLabel = line(raw.decline_label);
  if (!headline || !body || !rescueLabel || !declineLabel) return null;
  return { headline, body, rescueLabel, rescueDetail: line(raw.rescue_detail) || null, declineLabel };
};

// One display stamp/event per issuance for the life of the bundle, across
// card mounts (the server stamp is idempotent; the event should be too).
const displayedIssuances = new Set<string>();
export const markRecoveryIssuanceDisplayed = (issuanceId: string): boolean => {
  if (displayedIssuances.has(issuanceId)) return false;
  displayedIssuances.add(issuanceId);
  return true;
};
export const resetRecoveryDisplayedForTests = (): void => displayedIssuances.clear();

// The return surface (my-penmates CTA) deep-links into checkout with this
// flag; the checkout card consumes it once and presents the offer on arrival.
export const RECOVERY_RETURN_FLAG_PARAM = "recoveryOffer";

// The flag carries the draft id it was issued for; another letter's
// checkout never consumes it.
export const consumeRecoveryReturnFlag = (letterId: string): boolean => {
  if (typeof window === "undefined" || !letterId) return false;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get(RECOVERY_RETURN_FLAG_PARAM) !== String(letterId)) return false;
    url.searchParams.delete(RECOVERY_RETURN_FLAG_PARAM);
    window.history.replaceState(window.history.state, "", url.toString());
    return true;
  } catch {
    return false;
  }
};

export const withRecoveryTimeout = <T>(promise: Promise<T>, ms: number = RECOVERY_REQUEST_TIMEOUT_MS): Promise<T | null> =>
  new Promise<T | null>((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(null);
    }, ms);
    promise.then(
      (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      },
      () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(null);
      },
    );
  });

export type RecoveryTriggerDecision = { eligible: boolean; reason: string };

type RecoveryTriggerArgs = {
  trigger: RecoveryTrigger;
  letter: RawMessage | null;
  user: PmUser | null;
  requirements: CheckoutRequirements;
  isSending: boolean;
};

export const evaluateRecoveryTrigger = (args: RecoveryTriggerArgs): RecoveryTriggerDecision => {
  if (!checkoutRecoveryEnabled()) return { eligible: false, reason: "lever_off" };
  return evaluateCheckoutAbandonGate(args);
};

// Everything the recovery lever does not own. The regime D rescue rides the
// same abandon moments under its own, server-side switch, so the gate is
// shared and only the recovery half checks the lever.
export const evaluateCheckoutAbandonGate = (args: RecoveryTriggerArgs): RecoveryTriggerDecision => {
  const { trigger, letter, user, requirements, isSending } = args;
  if (!letter) return { eligible: false, reason: "no_letter" };
  // Already priced by an accepted offer: never asked again.
  if (letterHasRecoveryOffer(letter)) return { eligible: false, reason: "already_applied" };
  if (isSending) return { eligible: false, reason: "sending" };
  if (getIsResend(letter)) return { eligible: false, reason: "resend" };
  // A paid checkout is the only surface this offer exists on.
  if (!requirements.paymentRequired) return { eligible: false, reason: "payment_not_required" };
  // Glossy first letters route through the credits step; excluded from v1.
  if (getLetterPhotoType(letter) === "glossy") return { eligible: false, reason: "glossy" };
  // Postcards list below the offer price; never offered (server refuses too).
  const messageType = (letter as any)?.messageType ?? (letter as any)?.data?.messageType ?? (letter as any)?.order_type;
  if (typeof messageType === "string" && messageType.trim().toLowerCase() === "postcard") {
    return { eligible: false, reason: "postcard" };
  }
  // Cheap pre-filter on a durable fact only; the server remains the authority.
  if (user?.hasPaidInvoice === true) return { eligible: false, reason: "paid_invoice" };
  if (trigger === "payment_sheet_dismissed") {
    // Closing the sheet with a usable method already attached is not a
    // walk-away (summary mode); only a dismissal that leaves Payment
    // incomplete is.
    // `sections` holds required rows only; no Payment row means the sheet
    // was not the step being abandoned.
    const payment = requirements.sections.find((s) => s.key === "payment");
    if (!payment) return { eligible: false, reason: "payment_not_in_play" };
    if (payment.complete) return { eligible: false, reason: "payment_complete" };
  }
  return { eligible: true, reason: "eligible" };
};
